<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import {
  spDateKey,
  spMinutesSinceMidnight,
  spNowMinutes,
  formatTimeSP,
} from '../agendaDateUtils';
import { useMeetingDrag } from '../composables/useMeetingDrag';
import { useMeetingResize } from '../composables/useMeetingResize';
import { useFitsWidth } from '../composables/useFitsWidth';
import { computeOverlapLayout } from '../composables/useOverlapLayout';
import {
  meetingOpacityClass,
  statusLabelKey,
  STATUS_ORDER,
  isKnownStatus,
} from '../agendaStatus';

const props = defineProps({
  currentDate: { type: Date, required: true },
  meetings: { type: Array, default: () => [] },
  defaultDuration: { type: Number, default: 60 },
});

const emit = defineEmits(['meetingClick', 'newMeetingSlot', 'meetingDrop']);

const store = useStore();
const { t } = useI18n();

const openStatusDropdownId = ref(null);
const statusDropdownPos = ref({ top: 0, left: 0 });

const openStatusDropdownMeeting = computed(() =>
  props.meetings.find(m => m.id === openStatusDropdownId.value)
);

function statusLabel(status) {
  return isKnownStatus(status)
    ? t(`AGENDA.STATUS.${statusLabelKey(status)}`)
    : status;
}

function toggleStatusDropdown(meetingId, event) {
  const isOpening = openStatusDropdownId.value !== meetingId;
  openStatusDropdownId.value = isOpening ? meetingId : null;
  if (isOpening) {
    const rect = event.currentTarget.getBoundingClientRect();
    statusDropdownPos.value = { top: rect.bottom + 4, left: rect.left };
  }
}

async function selectStatus(meeting, status) {
  openStatusDropdownId.value = null;
  if (status === meeting.status) return;
  await store.dispatch('agenda/updateMeetingField', { id: meeting.id, status });
}

function onClickOutside(e) {
  if (!e.target.closest('[data-status-dropdown]')) {
    openStatusDropdownId.value = null;
  }
}

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', onClickOutside));

const { registerRow, fitsRow } = useFitsWidth();

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const scrollRef = ref(null);
const nowMinutes = ref(spNowMinutes());
const columnRefs = ref([]);

const visibleDays = computed(() => {
  const days = [];
  const start = new Date(props.currentDate);
  const dayOfWeek = start.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  start.setDate(start.getDate() + diff);
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
});

watch(visibleDays, () => {
  columnRefs.value = [];
});

let nowTimer = null;

onMounted(() => {
  nowTimer = setInterval(() => {
    nowMinutes.value = spNowMinutes();
  }, 60000);
  if (scrollRef.value) {
    scrollRef.value.scrollTop = Math.max(0, nowMinutes.value - 60);
  }
});

onUnmounted(() => clearInterval(nowTimer));

const todayKey = computed(() => spDateKey(new Date()));

const { onMeetingMousedown, dragState } = useMeetingDrag({
  visibleDays,
  columnRefs,
  emit,
});

const { onResizeMousedown, resizeState } = useMeetingResize({ emit });

function meetingsForDay(day) {
  const date = spDateKey(day);
  return props.meetings.filter(m => spDateKey(m.start_at) === date);
}

const overlapLayoutByDay = computed(() => {
  const map = new Map();
  visibleDays.value.forEach(day => {
    map.set(spDateKey(day), computeOverlapLayout(meetingsForDay(day)));
  });
  return map;
});

function meetingLayout(meeting, day) {
  const layout = overlapLayoutByDay.value.get(spDateKey(day))?.get(meeting.id);
  return layout || { left: 0, width: 100, zIndex: 10 };
}

function durationMinutes(meeting) {
  if (!meeting.start_at || !meeting.end_at) return props.defaultDuration;
  const start = new Date(meeting.start_at);
  const end = new Date(meeting.end_at);
  return Math.max(15, (end - start) / 60000);
}

function formatHour(hour) {
  return `${String(hour).padStart(2, '0')}:00`;
}

function onSlotClick(day, hour) {
  const start = new Date(day);
  start.setHours(hour, 0, 0, 0);
  const end = new Date(start);
  end.setMinutes(end.getMinutes() + props.defaultDuration);
  emit('newMeetingSlot', {
    start_at: start.toISOString(),
    end_at: end.toISOString(),
  });
}
</script>

<template>
  <div class="flex flex-col flex-1 overflow-hidden">
    <!-- Cabeçalho fixo -->
    <div class="flex flex-shrink-0 border-b border-n-weak bg-n-background">
      <div class="w-14 flex-shrink-0 border-r border-n-weak" />
      <div class="flex flex-1">
        <div
          v-for="day in visibleDays"
          :key="spDateKey(day)"
          class="flex-1 min-w-[100px] border-r border-n-weak last:border-r-0 h-10 flex flex-col items-center justify-center"
        >
          <span class="text-xs text-n-slate-10 uppercase">
            {{ day.toLocaleDateString('pt-BR', { weekday: 'short' }) }}
          </span>
          <span
            class="text-sm font-medium leading-tight"
            :class="
              spDateKey(day) === todayKey ? 'text-n-brand' : 'text-n-slate-12'
            "
          >
            {{ day.getDate() }}
          </span>
        </div>
      </div>
    </div>

    <!-- Corpo scrollável -->
    <div ref="scrollRef" class="flex flex-1 overflow-auto">
      <!-- Coluna de horários -->
      <div class="w-14 flex-shrink-0 border-r border-n-weak">
        <div class="relative">
          <div
            v-for="hour in HOURS"
            :key="hour"
            class="h-[60px] border-b border-n-weak flex items-start justify-end pr-2 pt-1"
          >
            <span class="text-xs text-n-slate-10">{{ formatHour(hour) }}</span>
          </div>
        </div>
      </div>

      <!-- Colunas dos dias -->
      <div class="flex flex-1">
        <div
          v-for="(day, dayIndex) in visibleDays"
          :key="spDateKey(day)"
          :ref="
            el => {
              if (el) columnRefs[dayIndex] = el;
            }
          "
          class="flex-1 min-w-[100px] border-r border-n-weak last:border-r-0"
        >
          <!-- Grade de slots -->
          <div class="relative">
            <div
              v-for="hour in HOURS"
              :key="hour"
              class="h-[60px] border-b border-n-weak hover:bg-n-alpha-1 cursor-pointer"
              @click="onSlotClick(day, hour)"
            />

            <!-- Linha "agora" -->
            <div
              v-if="spDateKey(day) === todayKey"
              class="absolute left-0 right-0 z-30 pointer-events-none"
              :style="{ top: `${nowMinutes}px` }"
            >
              <div class="flex items-center">
                <div
                  class="size-2 rounded-full bg-n-ruby-9 -ml-1 flex-shrink-0"
                />
                <div class="flex-1 h-px bg-n-ruby-9" />
              </div>
            </div>

            <!-- Eventos posicionados absolutamente -->
            <div
              v-for="meeting in meetingsForDay(day)"
              :key="meeting.id"
              class="absolute flex flex-col justify-start gap-0.5 rounded px-1 cursor-grab text-xs transition-opacity overflow-hidden text-white"
              :class="[
                meetingOpacityClass(meeting),
                dragState.draggingId === meeting.id
                  ? 'opacity-30'
                  : 'opacity-100',
                resizeState.resizingId === meeting.id
                  ? 'ring-2 ring-n-brand'
                  : '',
                durationMinutes(meeting) < 30 ? 'py-px' : 'py-0.5',
              ]"
              :style="{
                top: `${spMinutesSinceMidnight(meeting.start_at)}px`,
                height: `${durationMinutes(meeting)}px`,
                left: `${meetingLayout(meeting, day).left}%`,
                width: `calc(${meetingLayout(meeting, day).width}% - 2px)`,
                zIndex:
                  resizeState.resizingId === meeting.id
                    ? 100
                    : meetingLayout(meeting, day).zIndex,
                backgroundColor: meeting.user?.color,
              }"
              @mousedown.stop="
                onMeetingMousedown($event, meeting, $event.currentTarget)
              "
              @click.stop="
                dragState.draggingId === null &&
                  emit('meetingClick', meeting.id)
              "
            >
              <!-- >= 30min: título em cima, hora + status embaixo -->
              <template v-if="durationMinutes(meeting) >= 30">
                <div class="flex items-center justify-between gap-1 min-w-0">
                  <span
                    class="font-medium truncate leading-none flex items-center min-w-0"
                    :class="
                      durationMinutes(meeting) < 45
                        ? 'text-[10px] h-[13px]'
                        : 'h-[18px]'
                    "
                  >
                    {{ meeting.title }}
                  </span>
                  <a
                    v-if="meeting.google_meet_url"
                    :href="meeting.google_meet_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="i-lucide-arrow-up-right flex-shrink-0 text-white hover:opacity-80"
                    :class="
                      durationMinutes(meeting) < 45 ? 'size-2.5' : 'size-3'
                    "
                    @click.stop
                    @mousedown.stop
                  />
                </div>
                <div
                  class="flex items-center justify-between gap-1 min-w-0"
                  :class="
                    durationMinutes(meeting) < 45 ? 'h-[13px]' : 'h-[18px]'
                  "
                >
                  <span
                    class="truncate opacity-80 leading-none flex items-center min-w-0"
                    :class="
                      durationMinutes(meeting) < 45
                        ? 'text-[9px] h-[13px]'
                        : 'text-[11px] h-[18px]'
                    "
                  >
                    {{ formatTimeSP(meeting.start_at) }} –
                    {{ formatTimeSP(meeting.end_at) }}
                  </span>
                  <div
                    v-if="meetingLayout(meeting, day).width >= 50"
                    data-status-dropdown
                    class="flex-shrink-0 flex items-center"
                    :class="
                      durationMinutes(meeting) < 45 ? 'h-[13px]' : 'h-[18px]'
                    "
                    @click.stop
                  >
                    <button
                      class="flex items-center gap-0.5 rounded-full font-medium bg-white/20 text-white"
                      :class="[
                        durationMinutes(meeting) < 45
                          ? 'text-[8px] px-1 py-0 leading-[13px] h-[13px]'
                          : 'text-[10px] px-1.5 py-0 leading-[18px] h-[18px]',
                      ]"
                      @click="toggleStatusDropdown(meeting.id, $event)"
                    >
                      {{ statusLabel(meeting.status) }}
                      <span
                        class="i-lucide-chevron-down flex-shrink-0"
                        :class="
                          durationMinutes(meeting) < 45 ? 'size-2' : 'size-2.5'
                        "
                      />
                    </button>
                  </div>
                </div>
              </template>
              <!-- < 30min: título + hora (se couber) + status na mesma linha -->
              <div v-else class="relative h-[13px]">
                <!-- Medidor invisível: sempre com hora, decide se cabe -->
                <div
                  :ref="el => registerRow(meeting.id, el)"
                  class="absolute inset-0 flex items-center justify-between gap-1 invisible pointer-events-none"
                  aria-hidden="true"
                >
                  <span class="font-medium whitespace-nowrap leading-none">
                    {{ meeting.title }}
                  </span>
                  <span
                    class="whitespace-nowrap text-[9px] leading-none flex-shrink-0"
                  >
                    {{ formatTimeSP(meeting.start_at) }} –
                    {{ formatTimeSP(meeting.end_at) }}
                  </span>
                  <span
                    class="text-[8px] px-1 py-0 rounded-full border font-medium leading-[13px] h-[13px] flex-shrink-0"
                  >
                    {{ statusLabel(meeting.status) }}
                  </span>
                </div>

                <!-- Linha visível -->
                <div
                  class="absolute inset-0 flex items-center justify-between gap-1 min-w-0"
                >
                  <span
                    class="font-medium truncate leading-none flex items-center h-[13px] text-[10px] min-w-0"
                  >
                    {{ meeting.title }}
                  </span>
                  <span
                    v-if="fitsRow(meeting.id)"
                    class="whitespace-nowrap opacity-80 text-[9px] leading-none flex items-center h-[13px] flex-shrink-0"
                  >
                    {{ formatTimeSP(meeting.start_at) }} –
                    {{ formatTimeSP(meeting.end_at) }}
                  </span>
                  <div
                    v-if="meetingLayout(meeting, day).width >= 50"
                    data-status-dropdown
                    class="flex-shrink-0 flex items-center h-[13px]"
                    @click.stop
                  >
                    <button
                      class="flex items-center gap-0.5 text-[8px] px-1 py-0 rounded-full font-medium leading-[13px] h-[13px] bg-white/20 text-white"
                      @click="toggleStatusDropdown(meeting.id, $event)"
                    >
                      {{ statusLabel(meeting.status) }}
                      <span
                        class="i-lucide-chevron-down size-2 flex-shrink-0"
                      />
                    </button>
                  </div>
                </div>
              </div>

              <!-- Handles de resize -->
              <div
                class="absolute left-0 right-0 top-0 h-1.5 cursor-ns-resize z-20"
                @mousedown="
                  onResizeMousedown(
                    $event,
                    meeting,
                    day,
                    'start',
                    $event.currentTarget.parentElement
                  )
                "
              />
              <div
                class="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize z-20"
                @mousedown="
                  onResizeMousedown(
                    $event,
                    meeting,
                    day,
                    'end',
                    $event.currentTarget.parentElement
                  )
                "
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dropdown de status -->
    <Teleport to="body">
      <div
        v-if="openStatusDropdownId !== null"
        data-status-dropdown
        class="fixed z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 w-40"
        :style="{
          top: statusDropdownPos.top + 'px',
          left: statusDropdownPos.left + 'px',
        }"
        @mousedown.stop
        @click.stop
      >
        <button
          v-for="status in STATUS_ORDER"
          :key="status"
          class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
          :class="
            status === openStatusDropdownMeeting?.status
              ? 'text-n-brand font-medium'
              : 'text-n-slate-12'
          "
          @click="selectStatus(openStatusDropdownMeeting, status)"
        >
          {{ statusLabel(status) }}
          <span
            v-if="status === openStatusDropdownMeeting?.status"
            class="i-lucide-check size-3 text-n-brand ml-auto inline-block"
          />
        </button>
      </div>
    </Teleport>
  </div>
</template>
